import { useState, useCallback, useRef, useEffect } from 'react'
import { funnelApi } from '../api'
import type { PageBlock, SaveStatus, DevicePreview } from '../types'

const MAX_HISTORY = 30

/** Recursive JSON-compatible value type for structural comparison */
type DeepEqualValue = string | number | boolean | null | undefined | DeepEqualValue[] | { [key: string]: DeepEqualValue }

/** Lightweight deep-equal for PageBlock arrays (recursive structural comparison) */
function deepEqual(a: DeepEqualValue, b: DeepEqualValue): boolean {
  if (a === b) return true
  if (a == null || b == null) return false
  if (typeof a !== typeof b) return false
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, i) => deepEqual(item, b[i]))
  }
  if (typeof a === 'object' && typeof b === 'object') {
    const objA = a as Record<string, DeepEqualValue>
    const objB = b as Record<string, DeepEqualValue>
    const keysA = Object.keys(objA)
    const keysB = Object.keys(objB)
    if (keysA.length !== keysB.length) return false
    return keysA.every((k) => deepEqual(objA[k], objB[k]))
  }
  return false
}

/** Undo/redo hook extracted from UpsellBuilder */
function useUndoRedo<T>(initial: T) {
  const [state, setState] = useState(initial)
  const historyRef = useRef<T[]>([initial])
  const indexRef = useRef(0)

  const set = useCallback((newState: T | ((prev: T) => T)) => {
    setState((prev) => {
      const resolved = typeof newState === 'function'
        ? (newState as (prev: T) => T)(prev)
        : newState
      const lastInHistory = historyRef.current[indexRef.current]
      if (deepEqual(resolved as DeepEqualValue, lastInHistory as DeepEqualValue)) {
        return resolved
      }
      historyRef.current = historyRef.current.slice(0, indexRef.current + 1)
      historyRef.current.push(resolved)
      if (historyRef.current.length > MAX_HISTORY) {
        historyRef.current = historyRef.current.slice(-MAX_HISTORY)
      }
      indexRef.current = historyRef.current.length - 1
      return resolved
    })
  }, [])

  const reset = useCallback((newState: T) => {
    historyRef.current = [newState]
    indexRef.current = 0
    setState(newState)
  }, [])

  const undo = useCallback(() => {
    if (indexRef.current > 0) {
      indexRef.current--
      setState(historyRef.current[indexRef.current])
    }
  }, [])

  const redo = useCallback(() => {
    if (indexRef.current < historyRef.current.length - 1) {
      indexRef.current++
      setState(historyRef.current[indexRef.current])
    }
  }, [])

  const canUndo = indexRef.current > 0
  const canRedo = indexRef.current < historyRef.current.length - 1

  return { state, set, reset, undo, redo, canUndo, canRedo }
}

/**
 * Block CRUD operations (add, duplicate, delete, update, reorder)
 */
function useBlockOperations(
  setBlocks: (value: PageBlock[] | ((prev: PageBlock[]) => PageBlock[])) => void,
  blocks: PageBlock[],
  selectedBlockId: string | null,
  setSelectedBlockId: (id: string | null) => void
) {
  const addBlock = useCallback((type: string, defaultProps: Record<string, string | number | boolean>, afterId?: string) => {
    const newBlock: PageBlock = {
      id: `block-${Date.now()}`,
      type: type as PageBlock['type'],
      props: { ...defaultProps },
    }
    setBlocks((items) => {
      if (afterId) {
        const idx = items.findIndex((b) => b.id === afterId)
        if (idx >= 0) {
          const updated = [...items]
          updated.splice(idx + 1, 0, newBlock)
          return updated
        }
      }
      return [...items, newBlock]
    })
    setSelectedBlockId(newBlock.id)
    return newBlock
  }, [setBlocks, setSelectedBlockId])

  const duplicateBlock = useCallback((blockId: string) => {
    const block = blocks.find((b) => b.id === blockId)
    if (!block) return
    const idx = blocks.indexOf(block)
    const newBlock: PageBlock = {
      id: `block-${Date.now()}`,
      type: block.type,
      props: { ...block.props },
    }
    const updated = [...blocks]
    updated.splice(idx + 1, 0, newBlock)
    setBlocks(updated)
    setSelectedBlockId(newBlock.id)
  }, [blocks, setBlocks, setSelectedBlockId])

  const deleteBlock = useCallback((blockId: string) => {
    setBlocks(blocks.filter((b) => b.id !== blockId))
    if (selectedBlockId === blockId) setSelectedBlockId(null)
  }, [blocks, setBlocks, selectedBlockId, setSelectedBlockId])

  const updateBlockProps = useCallback((blockId: string, props: Record<string, string | number | boolean>) => {
    setBlocks(blocks.map((b) => (b.id === blockId ? { ...b, props } : b)))
  }, [blocks, setBlocks])

  const reorderBlocks = useCallback((activeId: string, overId: string) => {
    setBlocks((items) => {
      const oldIndex = items.findIndex((b) => b.id === activeId)
      const newIndex = items.findIndex((b) => b.id === overId)
      if (oldIndex < 0 || newIndex < 0) return items
      const arr = [...items]
      const [removed] = arr.splice(oldIndex, 1)
      arr.splice(newIndex, 0, removed)
      return arr
    })
  }, [setBlocks])

  return { addBlock, duplicateBlock, deleteBlock, updateBlockProps, reorderBlocks }
}

/**
 * Page save and export logic
 */
function usePagePersistence(
  funnelId: string | undefined,
  stepId: string | undefined,
  blocks: PageBlock[],
  savedContentRef: React.MutableRefObject<string>,
  setSaveStatus: (status: SaveStatus) => void
) {
  const handleSave = useCallback(async () => {
    if (!funnelId || !stepId) return
    setSaveStatus('saving')
    try {
      await funnelApi.saveStepPage(funnelId, stepId, JSON.stringify({ blocks }))
      savedContentRef.current = JSON.stringify(blocks)
      setSaveStatus('saved')
    } catch {
      setSaveStatus('error')
    }
  }, [funnelId, stepId, blocks, savedContentRef, setSaveStatus])

  const exportJson = useCallback(() => {
    const json = JSON.stringify({ stepId, funnelId, blocks }, null, 2)
    const blob = new Blob([json], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `upsell-page-${stepId || 'new'}.json`
    a.click()
    URL.revokeObjectURL(url)
  }, [funnelId, stepId, blocks])

  return { handleSave, exportJson }
}

export function usePageBuilder(funnelId?: string, stepId?: string) {
  const {
    state: blocks,
    set: setBlocks,
    reset: resetBlocks,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useUndoRedo<PageBlock[]>([])

  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null)
  const [showPreview, setShowPreview] = useState(false)
  const [devicePreview, setDevicePreview] = useState<DevicePreview>('desktop')
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [loading, setLoading] = useState(true)
  const savedContentRef = useRef<string>('')

  // Load existing page content
  useEffect(() => {
    if (!funnelId || !stepId) {
      setLoading(false)
      return
    }
    funnelApi.getStepPage(funnelId, stepId)
      .then((response) => {
        if (response.data) {
          try {
            const parsed = JSON.parse(response.data)
            if (parsed?.blocks && Array.isArray(parsed.blocks)) {
              resetBlocks(parsed.blocks)
              savedContentRef.current = JSON.stringify(parsed.blocks)
            }
          } catch { /* invalid JSON, start fresh */ }
        }
      })
      .catch(() => { /* step may not have page content yet */ })
      .finally(() => setLoading(false))
  }, [funnelId, stepId, resetBlocks])

  // Track unsaved changes
  useEffect(() => {
    if (loading) return
    const currentContent = JSON.stringify(blocks)
    const savedContent = savedContentRef.current || JSON.stringify([])
    if (currentContent !== savedContent) {
      setSaveStatus('unsaved')
    }
  }, [blocks, loading])

  const selectedBlock = blocks.find((b) => b.id === selectedBlockId) || null

  const { addBlock, duplicateBlock, deleteBlock, updateBlockProps, reorderBlocks } =
    useBlockOperations(setBlocks, blocks, selectedBlockId, setSelectedBlockId)

  const { handleSave, exportJson } =
    usePagePersistence(funnelId, stepId, blocks, savedContentRef, setSaveStatus)

  return {
    blocks,
    setBlocks,
    resetBlocks,
    selectedBlock,
    selectedBlockId,
    setSelectedBlockId,
    showPreview,
    setShowPreview,
    devicePreview,
    setDevicePreview,
    saveStatus,
    setSaveStatus,
    savedContentRef,
    loading,
    undo,
    redo,
    canUndo,
    canRedo,
    addBlock,
    duplicateBlock,
    deleteBlock,
    updateBlockProps,
    reorderBlocks,
    handleSave,
    exportJson,
  }
}
